import React, { useEffect, useCallback, useRef, useState } from 'react';

import { SatisfactionSurveyModal } from '@confluence/satisfaction-survey';
import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';

import { ExternalCollabSatisfactionSurvey } from './ExternalCollabSatisfactionSurvey';
import { useIsExternalCollaborator } from './useIsExternalCollaborator';

const MILLISECONDS_IN_ONE_DAY = 24 * 60 * 60 * 1000;

// When we want to show the survey on the next qualifying action.  This will allow us to not show
// the survey on the first action the guest does, but show it on the second such action
const NO_DELAY_TIME = 0;

// The time delay until we want to show the survey again if the user did not submit (answer) the
// survey, in milliseconds => currently 1 day
const DELAY_TIME_WHEN_SURVEY_NOT_SUBMITTED = MILLISECONDS_IN_ONE_DAY;

// The time delay until we want to show the survey again if the user did submit (answer) the
// survey, in milliseconds => currently 7 days
const DELAY_TIME_WHEN_SURVEY_SUBMITTED = 7 * MILLISECONDS_IN_ONE_DAY;

const getGuestSatisfactionSurveyNextSurveyTime = () => {
	const nextSurveyTimeValue =
		localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_GUEST_SURVEY_NEXT_TIME_TO_SHOW) ?? '0';
	return parseInt(nextSurveyTimeValue, 10);
};

export const setGuestSatisfactionSurveyNextSurveyTime = (millisecondsToNextSurveyTime: number) => {
	localStorage.setItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_GUEST_SURVEY_NEXT_TIME_TO_SHOW,
		Date.now() + millisecondsToNextSurveyTime,
	);
};

const shouldShowSurvey = (isGuestSurveyTriggered: boolean, isGuest: boolean): boolean => {
	if (isGuestSurveyTriggered && isGuest) {
		// Read the local storage setting to see if this guest has already submitted this survey, or
		// if we've shown this to them recently, or this is their first survey-triggering action
		const nextSurveyTime = getGuestSatisfactionSurveyNextSurveyTime();
		if (nextSurveyTime === 0) {
			// First triggering action - don't show the survey, but show it on the next triggering action
			setGuestSatisfactionSurveyNextSurveyTime(NO_DELAY_TIME);
			// (We're not showing the survey this time, so drop through to the final return below)
		} else if (Date.now() >= nextSurveyTime) {
			// Show the survey, but don't show it again for a while so as not to be annoying
			// (Note the next survey time may get overwritten if the user submits the survey)
			setGuestSatisfactionSurveyNextSurveyTime(DELAY_TIME_WHEN_SURVEY_NOT_SUBMITTED);
			return true;
		}
	}

	// We're not going to show the guest survey
	return false;
};

export type GuestSatisfactionSurveyWrapperProps = React.PropsWithChildren<{
	/** Guest survey trigger state */
	isGuestSurveyTriggered: boolean;

	/** Function called when we want to reset the Guest survey trigger */
	resetGuestSurveyTrigger: () => void;

	/** Where within the application this survey is being displayed (for analytics / tracking purposes). */
	source: string;
}>;

type GuestSurveyState = {
	// Whether the Guest survey needs to be shown. Having the "null" state avoids needing a
	// useEffect() to make sure the query data is only processed once
	isGuestSurveyActive: boolean | null;

	// Whether the Guest survey is currently visible.  If the user has submitted or dismissed the
	// Guest survey, we obviously don't want it to remain visible - but we also don't want to show
	// the children either - thus the separate state variable for visibility vs. active.
	isGuestSurveyVisible: boolean;
};

export const GuestSatisfactionSurveyWrapper = ({
	isGuestSurveyTriggered,
	resetGuestSurveyTrigger,
	source,
	children,
}: GuestSatisfactionSurveyWrapperProps) => {
	const [guestSurveyState, setGuestSurveyState] = useState<GuestSurveyState>({
		isGuestSurveyActive: null,
		isGuestSurveyVisible: false,
	});
	const { isGuestSurveyActive, isGuestSurveyVisible } = guestSurveyState;
	const prevIsGuestSurveyTriggered = useRef<boolean>(false);

	// Check if this user is a guest
	const { isExternalCollaborator: isGuest } = useIsExternalCollaborator();

	// We need to make sure the trigger is reset when the component is unmounted.  If not, the user
	// could move to another screen but the survey trigger would remain true.
	useEffect(() => {
		return () => {
			// Call resetGuestSurveyTrigger() on component unmount if needed
			resetGuestSurveyTrigger();
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
	// (Note that if dependencies are listed, the cleanup function is called every time one of those
	//  dependencies change, which results in "resetGuestSurveyTrigger()" being called 2 to 3 times
	//  when it should be called only once)

	// We want to reset our component's state on subsequent triggers so we can process those new
	// triggers from scratch.  Note we aren't using useEffect() here because it was being called even
	// when the trigger didn't change - so since we have to save the previous trigger state we might
	// as well do the comparison manually.
	if (isGuestSurveyTriggered && !prevIsGuestSurveyTriggered.current) {
		setGuestSurveyState({
			isGuestSurveyActive: null,
			isGuestSurveyVisible: false,
		});
	}
	prevIsGuestSurveyTriggered.current = isGuestSurveyTriggered;

	const handleSubmitted = useCallback(() => {
		// Don't show the survey for an even longer time than usual, to avoid annoying the user
		setGuestSatisfactionSurveyNextSurveyTime(DELAY_TIME_WHEN_SURVEY_SUBMITTED);
		// (no need to change the surveyState here, as this is done by "handleDone()")
	}, []);

	// handleDone() is called when the survey component is closed. If the user submitted the survey,
	// then handleDone() will be called after handleSubmitted(), as there is usually a "Thanks for
	// submitting" message shown in the survey component after submission.
	const handleDone = useCallback(() => {
		// Hide the Guest survey
		setGuestSurveyState({
			isGuestSurveyActive: true,
			isGuestSurveyVisible: false,
		});
	}, [setGuestSurveyState]);

	// Render pass data processing:

	// We have the user data, so determine whether to show the survey and reset the trigger
	if (isGuestSurveyActive === null) {
		const showSurvey = shouldShowSurvey(isGuestSurveyTriggered, isGuest);
		setGuestSurveyState({
			isGuestSurveyActive: showSurvey,
			isGuestSurveyVisible: showSurvey,
		});
		resetGuestSurveyTrigger();
		return null; // render survery or children on next render pass when state is updated
	}

	// If we decided that the Guest survey is active, we will show it until it is dismissed (and
	// nothing after it is dismissed, because we don't want to show the children - which likely
	//  is the Editor survey - right after this survey)
	if (isGuestSurveyActive) {
		return isGuestSurveyVisible ? (
			<SatisfactionSurveyModal size="medium">
				<ExternalCollabSatisfactionSurvey
					isGuest // (User has to be guest if we got here)
					onDone={handleDone}
					onSubmitted={handleSubmitted}
					source={source}
				/>
			</SatisfactionSurveyModal>
		) : null;
	} else {
		// else we decided not to show the survey, render the children instead
		return <>{children}</>;
	}
};
